import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { BedDouble, MoreVertical, Plus, Wrench } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT, Bi } from "@/lib/i18n";
import { inr } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { Chips } from "@/components/form";
import { BottomSheet } from "@/components/BottomSheet";
import { AddRoomsForm } from "@/components/AddRoomsForm";
import { AddTenantSheet } from "@/components/AddTenantSheet";
import { RoomAmenitiesSheet, RoomAmenityChips } from "@/components/RoomAmenities";

function BedChip({ bed, onVacant, onOccupied }: { bed: any; onVacant: () => void; onOccupied: () => void }) {
  const { t } = useT();
  const base = "min-h-[64px] rounded-xl p-2 text-left pg-tap flex flex-col justify-between";
  if (bed.status === "occupied")
    return (
      <button data-testid={`bed-chip-${bed.id}`} onClick={onOccupied} className={`${base} bg-emerald-50 border border-emerald-200 text-emerald-900`}>
        <span className="text-[11px] font-bold opacity-70">{t("bed")} {bed.label}</span>
        <span className="text-sm font-semibold truncate">{bed.tenant_name}</span>
      </button>
    );
  if (bed.status === "maintenance")
    return (
      <button data-testid={`bed-chip-${bed.id}`} onClick={onVacant} className={`${base} bg-orange-50 border border-orange-200 text-orange-800`}>
        <span className="text-[11px] font-bold opacity-70">{t("bed")} {bed.label}</span>
        <span className="text-xs font-semibold flex items-center gap-1"><Wrench className="h-3 w-3" />{t("bed_maintenance")}</span>
      </button>
    );
  return (
    <button data-testid={`bed-chip-${bed.id}`} onClick={onVacant} className={`${base} border-2 border-dashed border-slate-300 bg-white text-slate-500 hover:border-emerald-500`}>
      <span className="text-[11px] font-bold">{t("bed")} {bed.label}</span>
      <span className="text-xs font-semibold flex items-center gap-1"><Plus className="h-3 w-3" />{t("bed_vacant")}</span>
    </button>
  );
}

function RoomCard({ room, filter, onAddTenant, onChanged, onFeatures }: { room: any; filter: string; onAddTenant: (bedId: string) => void; onChanged: () => void; onFeatures: () => void }) {
  const { t } = useT();
  const nav = useNavigate();
  const beds = room.beds.filter((b) => filter === "all" || b.status === filter);
  if (!beds.length) return null;
  const act = async (fn: () => Promise<any>) => {
    try { await fn(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const vacantBed = room.beds.find((b) => b.status === "vacant");
  const maintBed = room.beds.find((b) => b.status === "maintenance");
  return (
    <Card className="p-4" data-testid={`room-card-${room.name}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-bold text-lg">{t("room")} {room.name}</p>
          <p className="text-xs text-slate-500">{room.sharing} {t("bed")} · {inr(room.rent_per_bed)} · {t("deposit")} {inr(room.deposit)}</p>
          <div className="mt-1.5"><RoomAmenityChips items={room.amenities} testId={`room-amenities-${room.name}`} /></div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid={`room-menu-${room.name}`} className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"><MoreVertical className="h-5 w-5 text-slate-500" /></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid={`room-features-${room.name}`} onClick={onFeatures}>Room features</DropdownMenuItem>
            {vacantBed && <DropdownMenuItem data-testid={`room-maint-${room.name}`} onClick={() => act(() => api.patch(`/beds/${vacantBed.id}`, { status: "maintenance" }))}>{t("mark_maintenance")} ({vacantBed.label})</DropdownMenuItem>}
            {maintBed && <DropdownMenuItem data-testid={`room-unmaint-${room.name}`} onClick={() => act(() => api.patch(`/beds/${maintBed.id}`, { status: "vacant" }))}>{t("mark_vacant")} ({maintBed.label})</DropdownMenuItem>}
            <DropdownMenuItem data-testid={`room-delete-${room.name}`} className="text-rose-600" onClick={() => window.confirm(`${t("delete_room")} ${room.name}?`) && act(() => api.delete(`/rooms/${room.id}`))}>{t("delete_room")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {beds.map((b) => (
          <BedChip key={b.id} bed={b} onVacant={() => (b.status === "vacant" ? onAddTenant(b.id) : act(() => api.patch(`/beds/${b.id}`, { status: "vacant" })))} onOccupied={() => nav(`/tenants/${b.tenant_id}`)} />
        ))}
      </div>
    </Card>
  );
}

export default function Beds() {
  const { t } = useT();
  const { property, reloadProperties } = useAuth();
  const { data, loading, error, reload } = useApi<any[]>(property ? `/properties/${property.id}/rooms` : null);
  const [filter, setFilter] = useState("all");
  const [roomsOpen, setRoomsOpen] = useState(false);
  const [tenantBed, setTenantBed] = useState<string | null>(null);
  const [featRoom, setFeatRoom] = useState<any>(null);
  const refresh = () => { reload(); reloadProperties(); };
  const addBtn = (
    <Button data-testid="beds-add-rooms-btn" onClick={() => setRoomsOpen(true)} className="h-11 rounded-full bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4 mr-1" />{t("add_rooms")}</Button>
  );
  return (
    <div>
      <PageTitle title={t("rooms_beds")} sub={t("tap_vacant")} right={addBtn} />
      <div className="mb-4 overflow-x-auto">
        <Chips testId="beds-filter" value={filter} onChange={setFilter} options={[
          { value: "all", label: t("all") }, { value: "vacant", label: t("bed_vacant") }, { value: "occupied", label: t("bed_occupied") }, { value: "maintenance", label: t("bed_maintenance") },
        ]} />
      </div>
      {loading && !data ? <ListSkeleton /> : error ? <ErrorState message={error} onRetry={reload} /> : !data?.length ? (
        <EmptyState testId="beds-empty" icon={BedDouble} title={t("no_rooms")} text={t("no_rooms_text")} action={addBtn} />
      ) : (
        <div className="space-y-3 pg-stagger">
          {data.map((r) => <RoomCard key={r.id} room={r} filter={filter} onAddTenant={setTenantBed} onChanged={refresh} onFeatures={() => setFeatRoom(r)} />)}
        </div>
      )}
      <BottomSheet open={roomsOpen} onOpenChange={setRoomsOpen} title={<Bi k="add_rooms" />} testId="add-rooms-sheet">
        {property && <AddRoomsForm propertyId={property.id} onDone={() => { setRoomsOpen(false); refresh(); }} />}
      </BottomSheet>
      <RoomAmenitiesSheet room={featRoom} onClose={() => setFeatRoom(null)} onSaved={reload} />
      <AddTenantSheet open={!!tenantBed} bedId={tenantBed || undefined} onClose={() => setTenantBed(null)} onSaved={refresh} />
    </div>
  );
}
